import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Foundation, Slide } from '../../types';
import { useInAppDialog } from '../Common/InAppDialog';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save, 
  Play, 
  Pause, 
  Image as ImageIcon, 
  FileText, 
  HelpCircle, 
  Layout, 
  Video, 
  Upload, 
  Palette, 
  Check, 
  Info, 
  MoreVertical, 
  Sparkles,
  ChevronRight,
  BookOpen,
  ArrowUp,
  ArrowDown,
  Maximize2,
  Sliders,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronUp,
  Link,
  Bold,
  List,
  ListOrdered,
  Lock,
  Unlock,
  CheckCircle2,
  Grid
} from 'lucide-react';

interface FoundationEditorProps {
  foundationId: string;
  onBack: () => void;
}

const PREMIUM_STOCK_ASSETS = [
  {
    name: 'Workplace Teamwork (Video)',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-business-partners-analyzing-data-on-a-digital-tablet-41618-large.mp4'
  },
  {
    name: 'Office Collaboration (Video)',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-group-of-businesspeople-having-a-meeting-in-office-41589-large.mp4'
  },
  {
    name: 'Active Listening (Video)',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-man-and-woman-having-a-discussion-in-office-41571-large.mp4'
  },
  {
    name: 'Professional Seminar',
    type: 'image',
    url: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=800&q=80'
  },
  {
    name: 'Team Alignment Huddle',
    type: 'image',
    url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80'
  },
  {
    name: 'Client Feedback Call',
    type: 'image',
    url: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=800&q=80'
  }
];

export const FoundationEditor: React.FC<FoundationEditorProps> = ({ foundationId, onBack }) => {
  const { showAlert, showPrompt } = useInAppDialog();
  const [foundation, setFoundation] = useState<Foundation | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [activePropertyTab, setActivePropertyTab] = useState<'Content' | 'Media' | 'Style'>('Content');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Media Upload States
  const [mediaUploadProgress, setMediaUploadProgress] = useState<number | null>(null);
  const [mediaUploadError, setMediaUploadError] = useState<string | null>(null);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Rich Text Editor textarea reference
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const fetchFoundation = async () => {
      try {
        const docRef = doc(db, 'foundations', foundationId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as Foundation;
          
          // Populate sensible defaults for any legacy fields
          const sanitizedSlides = (data.slides || []).map((slide: any) => ({
            id: slide.id || 'slide_' + Math.random().toString(36).substring(2, 9),
            type: slide.type || 'text',
            title: slide.title || 'Slide Title',
            content: slide.content || '',
            secondaryContent: slide.secondaryContent || '',
            layout: slide.layout || 'full',
            assetType: slide.assetType || 'none',
            assetUrl: slide.assetUrl || '',
            fontSizeHeader: slide.fontSizeHeader || 'text-3xl',
            fontSizeBody: slide.fontSizeBody || 'text-sm',
            containerSize: slide.containerSize || 'focused',
            alignX: slide.alignX || 'center',
            alignY: slide.alignY || 'center',
            backgroundColor: slide.backgroundColor || '#FAF6F0',
            textColor: slide.textColor || '#1E293B',
            minViewTime: slide.minViewTime || 0,
            skippable: slide.skippable !== undefined ? slide.skippable : true,
            quizType: slide.quizType || 'mcq',
            matchingPairs: slide.matchingPairs || [
              { left: 'Active Listening', right: 'Mirroring speaker sentiments' },
              { left: 'Critical Thinking', right: 'Sourcing reliable root causes' },
              { left: 'Agile Diagnostics', right: 'Iterating through prototypes' },
              { left: 'Constructive Feedback', right: 'Stating growth opportunities' },
              { left: 'Punctual Attendance', right: 'Arriving ten minutes prior' }
            ],
            quiz: slide.quiz || {
              question: 'Sample Quiz Question: Choose the correct approach?',
              options: ['Option A: Plan and review', 'Option B: Ignore and continue', 'Option C: Postpone feedback', 'Option D: Avoid collaboration'],
              correctAnswer: 'Option A: Plan and review',
              correctIndex: 0
            }
          }));

          setFoundation({
            ...data,
            id: docSnap.id,
            slides: sanitizedSlides
          });
        }
      } catch (err) {
        console.error('Error fetching foundation details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFoundation();
  }, [foundationId]);

  const handleUpdateSlide = (updatedSlide: Slide) => {
    if (!foundation) return;
    const newSlides = [...foundation.slides];
    newSlides[activeSlideIndex] = updatedSlide;
    setFoundation({ ...foundation, slides: newSlides });
  };

  const handleMediaUpload = (file: File) => {
    if (!foundation) return;
    const currentSlide = foundation.slides[activeSlideIndex];
    if (!currentSlide) return;

    // Validate type: support both images and videos
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      setMediaUploadError('Invalid file type. Please upload an image or video file.');
      return;
    }

    setIsMediaUploading(true);
    setMediaUploadProgress(0);
    setMediaUploadError(null);

    const fileExtension = file.name.split('.').pop() || '';
    const uniqueFileName = `wbl-media/media-${foundationId}-${currentSlide.id}-${Date.now()}.${fileExtension}`;
    const storageRef = ref(storage, uniqueFileName);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setMediaUploadProgress(Math.round(progress));
      },
      (err) => {
        console.error('WBL Media upload error:', err);
        setMediaUploadError(`Upload failed: ${err.message}`);
        setIsMediaUploading(false);
        setMediaUploadProgress(null);
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          // Set assetType and assetUrl on active slide
          handleUpdateSlide({
            ...currentSlide,
            assetType: isImage ? 'image' : 'video',
            assetUrl: downloadUrl
          });
          setIsMediaUploading(false);
          setMediaUploadProgress(null);
          setMediaUploadError(null);
        } catch (e: any) {
          console.error('Failed to retrieve download link:', e);
          setMediaUploadError(`Failed to fetch link: ${e.message}`);
          setIsMediaUploading(false);
          setMediaUploadProgress(null);
        }
      }
    );
  };

  const handleSave = async () => {
    if (!foundation) return;
    setSaveStatus('saving');
    try {
      await updateDoc(doc(db, 'foundations', foundationId), {
        slides: foundation.slides
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Failed to sync slide deck changes:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handleAddSlide = () => {
    if (!foundation) return;
    const newSlide: Slide = {
      id: 'slide_' + Date.now().toString(),
      type: 'text',
      title: 'New Workspace Slide',
      content: 'Define professional co-op learnings and educational summaries here.',
      secondaryContent: 'Secondary learning prompts go here for split alignments.',
      layout: 'full',
      assetType: 'none',
      assetUrl: '',
      fontSizeHeader: 'text-3xl',
      fontSizeBody: 'text-sm',
      containerSize: 'focused',
      alignX: 'center',
      alignY: 'center',
      backgroundColor: '#FAF6F0',
      textColor: '#1E293B',
      minViewTime: 0,
      skippable: true,
      quizType: 'mcq',
      matchingPairs: [
        { left: 'Active Listening', right: 'Mirroring speaker sentiments' },
        { left: 'Critical Thinking', right: 'Sourcing reliable root causes' },
        { left: 'Agile Diagnostics', right: 'Iterating through prototypes' },
        { left: 'Constructive Feedback', right: 'Stating growth opportunities' },
        { left: 'Punctual Attendance', right: 'Arriving ten minutes prior' }
      ],
      quiz: {
        question: 'How do you demonstrate active listening during peer alignment sessions?',
        options: [
          'By offering immediate solutions without pauses',
          'By repeating core points and validating sentiments',
          'By taking notes silently without visual agreement',
          'By waiting for other representatives to speak'
        ],
        correctAnswer: 'By repeating core points and validating sentiments',
        correctIndex: 1
      }
    };
    const newSlides = [...foundation.slides, newSlide];
    setFoundation({ ...foundation, slides: newSlides });
    setActiveSlideIndex(newSlides.length - 1);
  };

  const handleDeleteSlide = (indexToDelete: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!foundation) return;
    if (foundation.slides.length <= 1) {
      showAlert({
        title: "Cannot Delete Slide",
        message: "Your curriculum module must retain at least one slide.",
        type: "warning"
      });
      return;
    }
    const newSlides = foundation.slides.filter((_, idx) => idx !== indexToDelete);
    setFoundation({ ...foundation, slides: newSlides });
    
    // Auto-adjust active slide index
    if (activeSlideIndex >= newSlides.length) {
      setActiveSlideIndex(newSlides.length - 1);
    }
  };

  // Up/down thumbnail reordering actions
  const handleMoveSlide = (index: number, direction: 'up' | 'down', e: React.MouseEvent) => {
    e.stopPropagation();
    if (!foundation) return;
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= foundation.slides.length) return;

    const reordered = [...foundation.slides];
    const temp = reordered[index];
    reordered[index] = reordered[targetIdx];
    reordered[targetIdx] = temp;

    setFoundation({ ...foundation, slides: reordered });
    setActiveSlideIndex(targetIdx);
  };

  // Formatting actions mimicking "Rich Text Editor" (Tiptap-like custom toolbar)
  const insertRichFormat = async (tag: 'bold' | 'list' | 'list-ordered' | 'link') => {
    const textarea = bodyTextareaRef.current;
    if (!textarea || !activeSlide) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const originalText = textarea.value;
    const selected = originalText.substring(start, end);

    let formatted = '';
    switch (tag) {
      case 'bold':
        formatted = `**${selected || 'bold text'}**`;
        break;
      case 'list':
        formatted = `\n- ${selected || 'list item'}`;
        break;
      case 'list-ordered':
        formatted = `\n1. ${selected || 'numbered item'}`;
        break;
      case 'link': {
        const url = await showPrompt({
          title: 'Insert Link',
          message: 'Enter the link destination URL (e.g. https://example.com):',
          defaultValue: 'https://',
          placeholder: 'https://'
        });
        if (url !== null && url.trim()) {
          formatted = `[${selected || 'link text'}](${url.trim()})`;
        } else {
          return;
        }
        break;
      }
    }

    const nextText = originalText.substring(0, start) + formatted + originalText.substring(end);
    handleUpdateSlide({ ...activeSlide, content: nextText });

    // Focus and restore cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formatted.length, start + formatted.length);
    }, 50);
  };

  // Convert basic markdown tags to structured JSX strings for high-fidelity WYSIWYG rendering
  const renderRichText = (txt: string) => {
    if (!txt) return null;
    return txt.split('\n').map((line, idx) => {
      let content: React.ReactNode = line;
      
      // Bold checks
      if (line.startsWith('**') && line.endsWith('**')) {
        content = <strong className="font-extrabold">{line.slice(2, -2)}</strong>;
      } else if (line.includes('**')) {
        const parts = line.split('**');
        content = parts.map((part, pIdx) => pIdx % 2 === 1 ? <strong key={pIdx} className="font-extrabold">{part}</strong> : part);
      }

      // List checks
      if (line.startsWith('- ')) {
        return (
          <li key={idx} className="list-disc ml-4 my-1">
            {line.substring(2)}
          </li>
        );
      }
      if (line.match(/^\d+\.\s/)) {
        return (
          <li key={idx} className="list-decimal ml-4 my-1">
            {line.replace(/^\d+\.\s/, '')}
          </li>
        );
      }

      return <p key={idx} className="my-1.5 min-h-[1em]">{content}</p>;
    });
  };

  if (loading) {
    return (
      <div className="h-96 flex flex-col justify-center items-center text-slate-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0094c8] mb-4"></div>
        <span className="text-sm font-semibold uppercase">Booting WBL Creator Workstation...</span>
      </div>
    );
  }

  if (!foundation) {
    return (
      <div className="p-8 text-center text-slate-500">
        <p className="font-bold">Module not found.</p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-slate-100 rounded-lg text-xs">Back</button>
      </div>
    );
  }

  const activeSlide: Slide | null = foundation.slides[activeSlideIndex] || null;

  return (
    <div className="flex flex-col bg-[#f3f4f6] min-h-[calc(100vh-80px)] rounded-3xl overflow-hidden shadow-xl border border-slate-200">
      
      {/* Top action header bar */}
      <div className="bg-white px-6 py-4 border-b border-slate-200 flex justify-between items-center z-10">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack} 
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 hover:bg-slate-50 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Library
          </button>
          <span className="h-4 w-px bg-slate-200"></span>
          <div>
            <div className="flex items-center gap-2">
              <span className="bg-sky-50 text-sky-600 text-[9px] font-black uppercase px-2.5 py-0.5 rounded border border-sky-100">
                Pillar: {foundation.pillar || 'Communication'}
              </span>
            </div>
            <h1 className="text-sm font-black text-slate-800 mt-0.5 flex items-center gap-2 uppercase tracking-wide">
              <BookOpen className="w-4 h-4 text-[#00a3e0]" />
              <span>{foundation.title}</span>
            </h1>
          </div>
        </div>

        {/* Sync state indicators */}
        <div className="flex items-center gap-4">
          {saveStatus === 'saving' && (
            <span className="text-xs text-sky-600 flex items-center gap-1.5 font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-ping"></span>
              Syncing Change Log...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-xs text-emerald-600 flex items-center gap-1.5 font-black uppercase tracking-wider">
              <Check className="w-4 h-4 text-emerald-600" />
              Changes Synced!
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-xs text-rose-600 font-bold">Failed to Sync.</span>
          )}

          <button 
            onClick={handleSave} 
            className="bg-[#00a3e0] hover:bg-[#008cc0] text-white px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-[#00a3e0]/25 transition-all transform active:scale-95"
          >
            <Save className="w-4 h-4" /> Sync Changes
          </button>
        </div>
      </div>

      {/* Main interactive grid editor (Fixed Height 3-Pane WYSIWYG) */}
      <div className="flex-1 grid grid-cols-[240px_1fr_320px] md:grid-cols-[280px_1fr_360px] h-[780px] overflow-hidden">
        
        {/* LEFT PANE: CURRICULUM SEQUENCE NAVIGATOR (Width: 280px) */}
        <div className="bg-white border-r border-slate-200 p-5 flex flex-col gap-4 overflow-y-auto h-full text-slate-800">
          <div className="flex justify-between items-center pb-4 border-b border-slate-100">
            <span className="text-xs font-black uppercase text-slate-800 tracking-widest">Navigator</span>
            <span className="bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black px-2.5 py-1 rounded">
              {foundation.slides.length} SLIDES
            </span>
          </div>

          <div className="space-y-4 flex-1">
            {foundation.slides.map((slide, index) => {
              const isActive = activeSlideIndex === index;
              const isQuiz = slide.type === 'quiz';
              const isMediaSlide = slide.assetType !== 'none' && slide.assetUrl;
              
              return (
                <div key={slide.id} className="group relative">
                  {/* Aspect-ratio rectangular card thumbnail representing a true slide preview */}
                  <div 
                    onClick={() => setActiveSlideIndex(index)}
                    className="cursor-pointer aspect-[16/10] rounded-xl relative overflow-hidden transition-all duration-200 border-2 flex flex-col"
                    style={{ 
                      backgroundColor: slide.backgroundColor || '#ffffff', 
                      color: slide.textColor || '#0f172a',
                      borderColor: isActive ? '#00a3e0' : 'rgba(148, 163, 184, 0.3)',
                      boxShadow: isActive ? '0 10px 15px -3px rgba(0, 163, 224, 0.25), 0 4px 6px -4px rgba(0, 163, 224, 0.25)' : 'none'
                    }}
                  >
                    
                    {/* Index Number Badge */}
                    <div className="absolute top-1.5 left-1.5 z-20 bg-[#64748b] text-white text-[9px] font-bold w-4.5 h-4.5 rounded flex items-center justify-center shadow-sm">
                      {index + 1}
                    </div>

                    {/* True slide layout visual mock representation */}
                    {isQuiz ? (
                      <div className="w-full h-full flex flex-col justify-between p-2 select-none relative z-10" style={{ color: slide.textColor || '#0f172a' }}>
                        <div className="flex justify-between items-center w-full pl-5">
                          <span className="text-[6.5px] font-extrabold uppercase tracking-widest opacity-60">Knowledge Check</span>
                          <HelpCircle className="w-2.5 h-2.5 opacity-60" />
                        </div>
                        <div className="my-auto text-center px-1">
                          <span className="text-[7.5px] font-black leading-tight line-clamp-2 uppercase">
                            {slide.quiz?.question || 'Assess understanding'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 w-full mt-0.5">
                          <div className="bg-slate-500/10 border border-slate-500/15 rounded-[3px] py-0.5 px-1 text-[5px] font-bold text-center truncate">Option A</div>
                          <div className="bg-slate-500/10 border border-slate-500/15 rounded-[3px] py-0.5 px-1 text-[5px] font-bold text-center truncate">Option B</div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Render full background image/video cover if full or split backdrop */}
                        {isMediaSlide && (slide.layout === 'full' || slide.layout === 'split') && (
                          <div className="absolute inset-0 w-full h-full z-0 overflow-hidden pointer-events-none">
                            {slide.assetType === 'video' ? (
                              <div className="w-full h-full bg-slate-900/40 flex items-center justify-center">
                                <Video className="w-5 h-5 text-white/30" />
                              </div>
                            ) : (
                              <img 
                                src={slide.assetUrl} 
                                alt="Slide thumbnail backdrop" 
                                className="w-full h-full object-cover opacity-35" 
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                          </div>
                        )}

                        {/* Layout contents */}
                        {slide.layout === 'media-left' ? (
                          <div className="w-full h-full flex gap-1.5 p-2 select-none relative z-10">
                            {/* Mini media container left */}
                            <div className="w-[35%] h-full rounded bg-slate-100/10 border border-slate-400/20 overflow-hidden flex-shrink-0 flex items-center justify-center">
                              {isMediaSlide ? (
                                slide.assetType === 'video' ? (
                                  <Video className="w-3.5 h-3.5 opacity-60" />
                                ) : (
                                  <img src={slide.assetUrl} className="w-full h-full object-cover" />
                                )
                              ) : (
                                <ImageIcon className="w-3.5 h-3.5 opacity-40" />
                              )}
                            </div>
                            {/* Mini description right */}
                            <div className="flex-1 flex flex-col justify-center text-left min-w-0 pl-1">
                              <span className="text-[8px] font-black uppercase tracking-tight line-clamp-2 leading-tight">
                                {slide.title || 'NEW SLIDE'}
                              </span>
                              <span className="text-[5px] opacity-60 truncate mt-0.5">
                                {slide.content?.replace(/[#*`\n]/g, ' ') || 'Slide content facts...'}
                              </span>
                            </div>
                          </div>
                        ) : slide.layout === 'media-right' ? (
                          <div className="w-full h-full flex gap-1.5 p-2 select-none relative z-10">
                            {/* Mini description left */}
                            <div className="flex-1 flex flex-col justify-center text-left min-w-0 pl-4">
                              <span className="text-[8px] font-black uppercase tracking-tight line-clamp-2 leading-tight">
                                {slide.title || 'NEW SLIDE'}
                              </span>
                              <span className="text-[5px] opacity-60 truncate mt-0.5">
                                {slide.content?.replace(/[#*`\n]/g, ' ') || 'Slide content facts...'}
                              </span>
                            </div>
                            {/* Mini media container right */}
                            <div className="w-[35%] h-full rounded bg-slate-100/10 border border-slate-400/20 overflow-hidden flex-shrink-0 flex items-center justify-center">
                              {isMediaSlide ? (
                                slide.assetType === 'video' ? (
                                  <Video className="w-3.5 h-3.5 opacity-60" />
                                ) : (
                                  <img src={slide.assetUrl} className="w-full h-full object-cover" />
                                )
                              ) : (
                                <ImageIcon className="w-3.5 h-3.5 opacity-40" />
                              )}
                            </div>
                          </div>
                        ) : slide.layout === 'split' ? (
                          <div className="w-full h-full flex gap-1.5 p-2 select-none relative z-10">
                            {/* Left column */}
                            <div className="w-1/2 flex flex-col justify-center text-left min-w-0 pl-4">
                              <span className="text-[8px] font-black uppercase tracking-tight line-clamp-2 leading-tight">
                                {slide.title || 'NEW SLIDE'}
                              </span>
                              <span className="text-[5px] opacity-65 truncate mt-0.5">
                                {slide.content?.replace(/[#*`\n]/g, ' ') || 'Slide facts...'}
                              </span>
                            </div>
                            {/* Right column */}
                            <div className="w-1/2 flex flex-col justify-center text-left min-w-0 border-l border-current/10 pl-1">
                              <span className="text-[5px] opacity-70 line-clamp-3 leading-normal">
                                {slide.secondaryContent || 'Details...'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          // Full screen default layout
                          <div className="w-full h-full flex flex-col justify-center items-center p-2 text-center select-none relative z-10">
                            <span className="text-[8px] font-black uppercase tracking-tight line-clamp-2 leading-tight px-1 pl-4">
                              {slide.title || 'NEW SLIDE'}
                            </span>
                            <span className="text-[5px] opacity-70 line-clamp-2 mt-0.5 max-w-[85%] leading-normal">
                              {slide.content?.replace(/[#*`\n]/g, ' ') || 'Interactive learning slide...'}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Sequence adjustment buttons (only if hovered or active) */}
                    <div className="absolute bottom-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 p-0.5 rounded-md border border-slate-200 shadow-md z-30">
                      <button
                        disabled={index === 0}
                        onClick={(e) => handleMoveSlide(index, 'up', e)}
                        className="p-1 hover:bg-slate-100 text-slate-600 disabled:opacity-30 rounded transition-all"
                        title="Move Sequence Up"
                      >
                        <ArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        disabled={index === foundation.slides.length - 1}
                        onClick={(e) => handleMoveSlide(index, 'down', e)}
                        className="p-1 hover:bg-slate-100 text-slate-600 disabled:opacity-30 rounded transition-all"
                        title="Move Sequence Down"
                      >
                        <ArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button 
                        onClick={(e) => handleDeleteSlide(index, e)}
                        className="p-1 hover:bg-rose-50 text-rose-600 rounded transition-all"
                        title="Delete Slide Node"
                      >
                        <Trash2 className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                  {/* Label below card */}
                  <div className="mt-2 pl-1">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      {isQuiz ? 'Check' : 'Canvas'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <button 
            onClick={handleAddSlide}
            className="w-full py-4 border border-dashed border-slate-300 hover:border-[#00a3e0] rounded-2xl flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 transition-all font-bold text-xs mt-auto"
          >
            <Plus className="w-5 h-5" />
            Add Slide Node
          </button>
        </div>

        {/* CENTER PANE: LIVE WYSIWYG CANVAS PREVIEW */}
        <div className="bg-[#eef1f6] p-8 flex flex-col justify-center items-center overflow-y-auto h-full relative">
          
          {activeSlide ? (
            <div className="flex flex-col items-center justify-center flex-1 w-full max-w-4xl mx-auto py-4">
              
              {/* Main Presentation Screen Box */}
              <div 
                className="w-full aspect-[16/9] rounded-3xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden relative transition-all duration-300 bg-black"
              >
                
                {/* Responsive dynamic structure depending on styles and layouts */}
                <div className={`flex-1 flex flex-col justify-center h-full relative overflow-hidden ${
                  activeSlide.containerSize === 'focused' ? 'max-w-3xl mx-auto w-full' : 'w-full'
                }`}
                style={{ 
                  backgroundColor: activeSlide.backgroundColor || '#FAF6F0', 
                  color: activeSlide.textColor || '#1E293B' 
                }}>
                  
                  {activeSlide.type === 'quiz' ? (
                    /* ASSESSMENT MODE PREVIEW SCREEN */
                    <div className={`w-full flex flex-col justify-between h-full p-10 text-${activeSlide.alignX}`}>
                      
                      <div className="text-center mb-4">
                        <span className="px-3 py-1 bg-amber-500/10 text-amber-500 border border-amber-500/20 text-[10px] font-black rounded-full uppercase tracking-widest">
                          Active Quiz Playcard
                        </span>
                        <h2 className={`${activeSlide.fontSizeHeader || 'text-3xl'} font-black tracking-tight mt-4 text-slate-800`}>
                          {activeSlide.quiz?.question || 'Your interactive quiz question?'}
                        </h2>
                      </div>

                      {activeSlide.quizType === 'matching' ? (
                        /* MATCHING MODE PREVIEW */
                        <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto w-full text-xs">
                          {/* Left Items Column */}
                          <div className="space-y-2">
                            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Prompts (Left)</span>
                            {(activeSlide.matchingPairs || []).map((pair, idx) => (
                              <div key={idx} className="p-3 bg-white border border-slate-200 rounded-xl font-bold flex items-center gap-2 text-slate-800">
                                <span className="bg-sky-50 text-sky-500 w-4 h-4 rounded text-[9px] flex items-center justify-center font-black">{idx + 1}</span>
                                {pair.left}
                              </div>
                            ))}
                          </div>
                          {/* Right Shuffled Items Column */}
                          <div className="space-y-2">
                            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Targets (Right - Shuffled)</span>
                            {(activeSlide.matchingPairs || []).map((pair, idx) => (
                              <div key={idx} className="p-3 bg-white border border-slate-200 rounded-xl font-medium cursor-pointer hover:border-[#00a3e0] transition-all flex justify-between items-center text-slate-700">
                                <span>{pair.right}</span>
                                <span className="w-2.5 h-2.5 rounded-full border border-slate-300"></span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        /* MCQ MODE PREVIEW */
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto w-full">
                          {(activeSlide.quiz?.options || []).map((option: string, optIdx: number) => {
                            const isCorrect = optIdx === activeSlide.quiz?.correctIndex;
                            return (
                              <div 
                                key={optIdx} 
                                className={`p-3.5 rounded-xl border text-left font-semibold text-xs flex items-center justify-between transition-all ${
                                  isCorrect 
                                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-black' 
                                    : 'border-slate-200 hover:border-[#00a3e0] text-slate-700 bg-white'
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  <span className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center font-black text-[10px]">
                                    {String.fromCharCode(65 + optIdx)}
                                  </span>
                                  {option}
                                </span>
                                {isCorrect && (
                                  <span className="bg-emerald-500 text-white rounded-full p-0.5">
                                    <Check className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                    </div>
                  ) : (
                    /* DYNAMIC CONTENT LAYOUT STRUCTURES (e.g. video background playing) */
                    <div className="w-full h-full relative flex flex-col justify-center">
                      {activeSlide.assetType === 'video' && activeSlide.assetUrl ? (
                        <div className="absolute inset-0 w-full h-full bg-black z-0">
                          <video src={activeSlide.assetUrl} autoPlay loop muted className="w-full h-full object-cover opacity-90" />
                        </div>
                      ) : activeSlide.assetType === 'image' && activeSlide.assetUrl ? (
                        <div className="absolute inset-0 w-full h-full bg-black z-0">
                          <img src={activeSlide.assetUrl} alt="Visual backdrop" className="w-full h-full object-cover opacity-90" />
                        </div>
                      ) : null}

                      {/* Foreground Content Card overlay if there is a background or simple text */}
                      <div className={`relative z-10 p-10 h-full flex flex-col justify-center text-${activeSlide.alignX} items-${
                        activeSlide.alignX === 'center' ? 'center' : activeSlide.alignX === 'right' ? 'end' : 'start'
                      } ${activeSlide.assetType !== 'none' ? 'bg-black/40 text-white' : ''}`}>
                        
                        {activeSlide.layout === 'full' && (
                          <div className="space-y-4 max-w-2xl">
                            {activeSlide.assetType === 'none' && (
                              <div className="w-12 h-1 bg-[#00a3e0] rounded-full mb-2"></div>
                            )}
                            <h1 className={`${activeSlide.fontSizeHeader || 'text-4xl'} font-black tracking-tight leading-snug`}>
                              {activeSlide.title}
                            </h1>
                            <div className={`${activeSlide.fontSizeBody || 'text-sm'} opacity-90 leading-relaxed font-medium whitespace-pre-line max-w-3xl`}>
                              {renderRichText(activeSlide.content)}
                            </div>
                          </div>
                        )}

                        {activeSlide.layout === 'split' && (
                          <div className="grid grid-cols-2 gap-8 items-center w-full h-full text-left">
                            <div className="space-y-4">
                              <h1 className={`${activeSlide.fontSizeHeader || 'text-3xl'} font-black tracking-tight leading-snug`}>
                                {activeSlide.title}
                              </h1>
                              <div className={`${activeSlide.fontSizeBody || 'text-sm'} opacity-90 leading-relaxed font-medium whitespace-pre-line`}>
                                {renderRichText(activeSlide.content)}
                              </div>
                            </div>
                            <div className={`p-6 border border-dashed rounded-2xl space-y-3 ${activeSlide.assetType !== 'none' ? 'bg-black/50 border-white/20' : 'bg-white border-slate-200'}`}>
                              <span className="text-[10px] text-sky-400 font-extrabold uppercase tracking-wider flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5" /> Additional Context Node
                              </span>
                              <div className={`text-xs opacity-80 leading-relaxed font-medium whitespace-pre-line italic ${activeSlide.assetType !== 'none' ? 'text-slate-200' : 'text-slate-600'}`}>
                                {activeSlide.secondaryContent || 'Add supplementary facts or work-based learning checkpoints in the right-pane content editor.'}
                              </div>
                            </div>
                          </div>
                        )}

                        {activeSlide.layout === 'media-left' && (
                          <div className="grid grid-cols-12 gap-8 items-center w-full h-full text-left">
                            <div className="col-span-5 bg-black/20 rounded-2xl aspect-[4/3] flex items-center justify-center overflow-hidden relative border border-white/10 shadow-md">
                              {activeSlide.assetType !== 'none' && activeSlide.assetUrl ? (
                                activeSlide.assetType === 'video' ? (
                                  <video src={activeSlide.assetUrl} autoPlay loop muted className="w-full h-full object-cover" />
                                ) : (
                                  <img src={activeSlide.assetUrl} alt="Visual cover" className="w-full h-full object-cover" />
                                )
                              ) : (
                                <div className="text-center p-4">
                                  <ImageIcon className="w-8 h-8 opacity-40 mx-auto mb-1.5" />
                                  <span className="text-[9px] font-bold uppercase tracking-wider opacity-60">Media Placeholder</span>
                                </div>
                              )}
                            </div>
                            <div className="col-span-7 space-y-4">
                              <h1 className={`${activeSlide.fontSizeHeader || 'text-3xl'} font-black tracking-tight leading-snug`}>
                                {activeSlide.title}
                              </h1>
                              <div className={`${activeSlide.fontSizeBody || 'text-sm'} opacity-90 leading-relaxed font-medium whitespace-pre-line`}>
                                {renderRichText(activeSlide.content)}
                              </div>
                              {activeSlide.secondaryContent && (
                                <p className="text-xs opacity-70 italic font-medium">{activeSlide.secondaryContent}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {activeSlide.layout === 'media-right' && (
                          <div className="grid grid-cols-12 gap-8 items-center w-full h-full text-left">
                            <div className="col-span-7 space-y-4">
                              <h1 className={`${activeSlide.fontSizeHeader || 'text-3xl'} font-black tracking-tight leading-snug`}>
                                {activeSlide.title}
                              </h1>
                              <div className={`${activeSlide.fontSizeBody || 'text-sm'} opacity-90 leading-relaxed font-medium whitespace-pre-line`}>
                                {renderRichText(activeSlide.content)}
                              </div>
                              {activeSlide.secondaryContent && (
                                <p className="text-xs opacity-70 italic font-medium">{activeSlide.secondaryContent}</p>
                              )}
                            </div>
                            <div className="col-span-5 bg-black/20 rounded-2xl aspect-[4/3] flex items-center justify-center overflow-hidden relative border border-white/10 shadow-md">
                              {activeSlide.assetType !== 'none' && activeSlide.assetUrl ? (
                                activeSlide.assetType === 'video' ? (
                                  <video src={activeSlide.assetUrl} autoPlay loop muted className="w-full h-full object-cover" />
                                ) : (
                                  <img src={activeSlide.assetUrl} alt="Visual cover" className="w-full h-full object-cover" />
                                )
                              ) : (
                                <div className="text-center p-4">
                                  <ImageIcon className="w-8 h-8 opacity-40 mx-auto mb-1.5" />
                                  <span className="text-[9px] font-bold uppercase tracking-wider opacity-60">Media Placeholder</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  )}

                </div>



              </div>



            </div>
          ) : (
            <div className="text-center p-12 bg-white border border-slate-200 rounded-3xl max-w-md mx-auto my-auto shadow-sm">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h2 className="text-base font-bold text-slate-800">Workspace empty</h2>
              <p className="text-xs text-slate-500 mt-1">Configure your modules by adding a new slide stage.</p>
              <button onClick={handleAddSlide} className="mt-4 px-4 py-2 bg-[#00a3e0] text-white rounded-xl text-xs font-bold uppercase">Add Slide</button>
            </div>
          )}

        </div>

        {/* RIGHT PANE: CONTEXTUAL PROPERTIES PANEL (Width: 320px) */}
        <div className="bg-white border-l border-slate-200 flex flex-col overflow-y-auto h-full text-slate-800">
          
          {/* Header Panel */}
          <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white">
            <span className="text-xs font-black uppercase text-slate-800 tracking-widest">Properties</span>
            <span className="bg-[#00c5c5] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded">
              {activeSlide ? (activeSlide.type === 'quiz' ? 'QUIZ' : 'FULL') : 'FULL'}
            </span>
          </div>

          {/* Properties navigation tabs */}
          <div className="flex border-b border-slate-100 bg-white p-1">
            {['Content', 'Media', 'Style'].map((tab) => {
              const isSelected = activePropertyTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setActivePropertyTab(tab as any)}
                  className={`flex-1 py-3 text-[11px] font-black uppercase tracking-widest transition-all ${
                    isSelected 
                      ? 'bg-slate-100 text-slate-800 rounded' 
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          {/* Tab content space */}
          <div className="p-5 space-y-6">
            
            {activeSlide && activePropertyTab === 'Content' && (
              <div className="space-y-5">
                
                {/* SLIDE MODE Toggle between Content and Quiz */}
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Slide Mode</label>
                  <div className="grid grid-cols-2 gap-2 bg-[#f8fafc] border border-slate-100 p-1 rounded-xl">
                    <button
                      onClick={() => handleUpdateSlide({ ...activeSlide, type: 'text' })}
                      className={`py-2 rounded-lg flex flex-col items-center justify-center gap-1.5 font-bold text-[11px] transition-all ${
                        activeSlide.type !== 'quiz'
                          ? 'bg-[#00a3e0] text-white shadow'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Content
                    </button>
                    <button
                      onClick={() => handleUpdateSlide({ ...activeSlide, type: 'quiz' })}
                      className={`py-2 rounded-lg flex flex-col items-center justify-center gap-1.5 font-bold text-[11px] transition-all ${
                        activeSlide.type === 'quiz'
                          ? 'bg-[#00a3e0] text-white shadow'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                      Quiz
                    </button>
                  </div>
                </div>

                {activeSlide.type === 'quiz' ? (
                  /* QUIZ PROPERTIES LOGIC */
                  <div className="space-y-4 pt-3 border-t border-slate-100">
                    
                    {/* Quiz Type Select */}
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Quiz Type Selection</label>
                      <select
                        value={activeSlide.quizType || 'mcq'}
                        onChange={(e) => handleUpdateSlide({ ...activeSlide, quizType: e.target.value as any })}
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-xs font-bold focus:ring-1 focus:ring-[#00a3e0] bg-[#f8fafc] text-slate-800 outline-none"
                      >
                        <option value="mcq">Multiple Choice Question (MCQ)</option>
                        <option value="matching">Matching Pairs (Interactive)</option>
                      </select>
                    </div>

                    {activeSlide.quizType === 'matching' ? (
                      /* MATCHING PAIRS EDITOR */
                      <div className="space-y-3 pt-2">
                        <div className="p-3 bg-sky-50 border border-sky-100 rounded-lg text-[10px] text-sky-700 leading-relaxed font-semibold">
                          Define up to 5 matching prompt pairs. The student player shuffles the right column targets dynamically.
                        </div>
                        
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                          {(activeSlide.matchingPairs || []).map((pair, idx) => (
                            <div key={idx} className="p-2.5 bg-[#f8fafc] rounded-xl border border-slate-200 space-y-2">
                              <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
                                <span className="text-[9px] font-black text-[#00a3e0] uppercase">Pair {idx + 1}</span>
                              </div>
                              <div className="space-y-1">
                                <span className="text-[8px] font-bold text-slate-400 uppercase block">Left Prompt</span>
                                <input
                                  type="text"
                                  className="w-full p-2 border border-slate-200 bg-white rounded-lg text-xs text-slate-800 font-semibold focus:border-[#00a3e0]"
                                  value={pair.left}
                                  onChange={(e) => {
                                    const matchingPairs = [...(activeSlide.matchingPairs || [])];
                                    matchingPairs[idx] = { ...pair, left: e.target.value };
                                    handleUpdateSlide({ ...activeSlide, matchingPairs });
                                  }}
                                />
                              </div>
                              <div className="space-y-1">
                                <span className="text-[8px] font-bold text-slate-400 uppercase block">Right Target (Answer)</span>
                                <input
                                  type="text"
                                  className="w-full p-2 border border-slate-200 bg-white rounded-lg text-xs text-slate-700 font-medium focus:border-[#00a3e0]"
                                  value={pair.right}
                                  onChange={(e) => {
                                    const matchingPairs = [...(activeSlide.matchingPairs || [])];
                                    matchingPairs[idx] = { ...pair, right: e.target.value };
                                    handleUpdateSlide({ ...activeSlide, matchingPairs });
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      /* MCQ LOGIC */
                      <div className="space-y-3 pt-2">
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Question Title</label>
                          <textarea
                            className="w-full p-2.5 border border-slate-200 rounded-lg text-xs font-semibold focus:ring-1 focus:ring-[#00a3e0] bg-[#f8fafc] text-slate-800 outline-none"
                            rows={2}
                            value={activeSlide.quiz?.question || ''}
                            onChange={(e) => {
                              const quiz = { ...(activeSlide.quiz || { question: '', options: [], correctAnswer: '' }), question: e.target.value };
                              handleUpdateSlide({ ...activeSlide, quiz });
                            }}
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Options (Choices A-D)</label>
                          {(activeSlide.quiz?.options || []).map((option: string, oIdx: number) => {
                            const isCorrect = oIdx === activeSlide.quiz?.correctIndex;
                            return (
                              <div key={oIdx} className="flex gap-2 items-center bg-[#f8fafc] p-2 rounded-xl border border-slate-200">
                                <span className="text-[10px] font-black text-slate-500 w-5 text-center">{String.fromCharCode(65 + oIdx)}</span>
                                <input
                                  type="text"
                                  className="flex-1 p-1.5 border border-slate-200 bg-white rounded-lg text-xs text-slate-800 font-semibold focus:border-[#00a3e0]"
                                  value={option}
                                  onChange={(e) => {
                                    const options = [...(activeSlide.quiz?.options || [])];
                                    options[oIdx] = e.target.value;
                                    const quiz = { 
                                      ...(activeSlide.quiz || { question: '', options: [], correctAnswer: '' }), 
                                      options,
                                      correctAnswer: oIdx === activeSlide.quiz?.correctIndex ? e.target.value : (activeSlide.quiz?.correctAnswer || '')
                                    };
                                    handleUpdateSlide({ ...activeSlide, quiz });
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const quiz = { 
                                      ...(activeSlide.quiz || { question: '', options: [], correctAnswer: '' }), 
                                      correctIndex: oIdx,
                                      correctAnswer: option 
                                    };
                                    handleUpdateSlide({ ...activeSlide, quiz });
                                  }}
                                  className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${
                                    isCorrect
                                      ? 'bg-emerald-600 text-white shadow-sm'
                                      : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                                  }`}
                                  title="Identify correct index"
                                >
                                  Correct
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </div>
                ) : (
                  /* CONTENT MODULE SPECIFIC INPUTS */
                  <div className="space-y-4 pt-3 border-t border-slate-100">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Slide Title / Headline</label>
                      <input
                        type="text"
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-xs font-semibold focus:ring-1 focus:ring-[#00a3e0] bg-[#f8fafc] text-slate-800 outline-none"
                        value={activeSlide.title || ''}
                        onChange={(e) => handleUpdateSlide({ ...activeSlide, title: e.target.value })}
                        placeholder="e.g. Active Listening Methodology"
                      />
                    </div>

                    {/* Rich Text Editor: Tiptap-like visual component */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Primary Body Text (Rich)</label>
                        
                        {/* WYSIWYG Editor Toolbar */}
                        <div className="flex items-center gap-1 bg-[#f8fafc] px-2 py-1 rounded-lg border border-slate-200">
                          <button 
                            type="button"
                            onClick={() => insertRichFormat('bold')}
                            className="p-1 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-800"
                            title="Format Bold"
                          >
                            <Bold className="w-3 h-3" />
                          </button>
                          <button 
                            type="button"
                            onClick={() => insertRichFormat('list')}
                            className="p-1 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-800"
                            title="Bullet List"
                          >
                            <List className="w-3 h-3" />
                          </button>
                          <button 
                            type="button"
                            onClick={() => insertRichFormat('list-ordered')}
                            className="p-1 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-800"
                            title="Numbered List"
                          >
                            <ListOrdered className="w-3 h-3" />
                          </button>
                          <button 
                            type="button"
                            onClick={() => insertRichFormat('link')}
                            className="p-1 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-800"
                            title="Insert Link"
                          >
                            <Link className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      <textarea
                        ref={bodyTextareaRef}
                        className="w-full p-3 border border-slate-200 rounded-lg text-xs font-medium focus:ring-1 focus:ring-[#00a3e0] bg-[#f8fafc] text-slate-800 outline-none"
                        rows={7}
                        value={activeSlide.content || ''}
                        onChange={(e) => handleUpdateSlide({ ...activeSlide, content: e.target.value })}
                        placeholder="Define learning principles or communication milestones. Select text to format bold or insert lists."
                      />
                    </div>

                    {/* Secondary Content: Only visible if layout is split, media-left, or media-right */}
                    {(activeSlide.layout === 'split' || activeSlide.layout === 'media-left' || activeSlide.layout === 'media-right') && (
                      <div className="animate-in fade-in duration-300">
                        <label className="text-[10px] font-black text-[#00a3e0] uppercase tracking-widest block mb-1">Secondary Content Column</label>
                        <textarea
                          className="w-full p-2.5 border border-slate-200 rounded-lg text-xs font-medium focus:ring-1 focus:ring-[#00a3e0] bg-[#f8fafc] text-slate-800 outline-none"
                          rows={3}
                          value={activeSlide.secondaryContent || ''}
                          onChange={(e) => handleUpdateSlide({ ...activeSlide, secondaryContent: e.target.value })}
                          placeholder="Secondary learning checks, extra data visualizers, or feedback loops..."
                        />
                      </div>
                    )}

                  </div>
                )}

              </div>
            )}

            {activeSlide && activePropertyTab === 'Media' && (
              <div className="space-y-6">
                
                {/* LAYOUT ARCHITECTURE selection matrix */}
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3">Layout Architecture</label>
                  <div className="grid grid-cols-2 gap-2.5">
                    {[
                      { id: 'full', label: 'Full Page' },
                      { id: 'split', label: 'Split Page' },
                      { id: 'media-left', label: 'Media Left' },
                      { id: 'media-right', label: 'Media Right' }
                    ].map((lay) => (
                      <button
                        key={lay.id}
                        onClick={() => handleUpdateSlide({ ...activeSlide, layout: lay.id as any })}
                        className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-all ${
                          activeSlide.layout === lay.id
                            ? 'border-[#00a3e0] bg-sky-50 text-[#00a3e0] font-bold text-xs'
                            : 'border-slate-200 bg-[#f8fafc] text-slate-500 hover:border-slate-300 text-xs'
                        }`}
                      >
                        <Layout className="w-4 h-4 opacity-75" />
                        {lay.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Media Controller (None, Image, Video) */}
                <div className="pt-4 border-t border-slate-100 space-y-4">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Media Controller</span>
                  
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 block mb-1">Asset Channel</label>
                    <select
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-xs font-bold focus:ring-1 focus:ring-[#00a3e0] bg-[#f8fafc] text-slate-800 outline-none"
                      value={activeSlide.assetType || 'none'}
                      onChange={(e) => handleUpdateSlide({ ...activeSlide, assetType: e.target.value as any })}
                    >
                      <option value="none">None (Standard Text Canvas)</option>
                      <option value="image">Static Image / Infographic</option>
                      <option value="video">Motion Loop (MP4 Video)</option>
                    </select>
                  </div>

                  {activeSlide.assetType !== 'none' && (
                    <div className="space-y-4 animate-in slide-in-from-top-2">
                      <div>
                        <label className="text-[10px] font-bold text-slate-500 block mb-1">Persistent Resource URL</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-200 bg-[#f8fafc] rounded-lg text-xs font-mono text-slate-800 outline-none focus:border-[#00a3e0]"
                          value={activeSlide.assetUrl || ''}
                          onChange={(e) => handleUpdateSlide({ ...activeSlide, assetUrl: e.target.value })}
                          placeholder="https://images.unsplash.com/..."
                        />
                      </div>

                      {/* Stock Preset Picker */}
                      <div>
                        <span className="text-[9px] font-black text-[#00a3e0] flex items-center gap-1 uppercase tracking-wider mb-2">
                          <Sparkles className="w-3 h-3" /> Stock Premium Presets
                        </span>
                        <div className="space-y-1 max-h-36 overflow-y-auto border border-slate-200 rounded-xl p-1.5 bg-[#f8fafc]">
                          {PREMIUM_STOCK_ASSETS.map((asset, k) => (
                            <button
                              key={k}
                              onClick={() => handleUpdateSlide({
                                ...activeSlide,
                                assetType: asset.type as any,
                                assetUrl: asset.url
                              })}
                              className="w-full text-left p-1.5 hover:bg-slate-200 text-[10px] font-semibold text-slate-600 hover:text-slate-800 rounded flex items-center justify-between"
                            >
                              <span>{asset.name}</span>
                              <ChevronRight className="w-3 h-3 text-slate-400" />
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Integrated File Upload Drag & Drop Zone */}
                      <div>
                        <label className="text-[10px] font-bold text-slate-500 block mb-1">Upload WBL Media</label>
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                          }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            if (e.dataTransfer.files?.[0]) {
                              handleMediaUpload(e.dataTransfer.files[0]);
                            }
                          }}
                          className={`border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer ${
                            isDragging 
                              ? 'border-[#00a3e0] bg-sky-50' 
                              : 'border-slate-200 bg-[#f8fafc] hover:bg-slate-50 hover:border-slate-300'
                          }`}
                        >
                          <input 
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*,video/*"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleMediaUpload(e.target.files[0]);
                              }
                            }}
                          />
                          
                          {isMediaUploading ? (
                            <div className="flex flex-col items-center justify-center py-2">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00a3e0] mb-2"></div>
                              <span className="text-[10px] text-slate-700 font-bold block">Uploading to Cloud...</span>
                              <span className="text-[9px] text-[#00a3e0] font-black mt-1">{mediaUploadProgress}%</span>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1.5" />
                              <span className="text-[10px] text-slate-700 font-bold block">Upload to Storage</span>
                              <span className="text-[8px] text-slate-400 block mt-0.5">Drag & drop files or click to browse</span>
                            </>
                          )}
                        </div>

                        {mediaUploadError && (
                          <div className="mt-2 bg-red-50 border border-red-100 rounded-lg p-2.5">
                            <p className="text-[9px] font-bold text-red-600 leading-tight">
                              {mediaUploadError}
                            </p>
                          </div>
                        )}
                      </div>

                    </div>
                  )}

                </div>

              </div>
            )}

            {activeSlide && activePropertyTab === 'Style' && (
              <div className="space-y-5">
                
                {/* Headline & Body Typography Scale */}
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Typography Scale</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-[8px] font-bold text-slate-400 uppercase block mb-1">Headline Size</span>
                      <select
                        value={activeSlide.fontSizeHeader || 'text-3xl'}
                        onChange={(e) => handleUpdateSlide({ ...activeSlide, fontSizeHeader: e.target.value })}
                        className="w-full p-2 border border-slate-200 rounded-lg text-[10px] font-bold bg-[#f8fafc] text-slate-800 outline-none"
                      >
                        <option value="text-xl">Small (XL)</option>
                        <option value="text-2xl">Medium (2XL)</option>
                        <option value="text-3xl">Standard (3XL)</option>
                        <option value="text-4xl">Large (4XL)</option>
                        <option value="text-5xl">Extra Large (5XL)</option>
                      </select>
                    </div>
                    <div>
                      <span className="text-[8px] font-bold text-slate-400 uppercase block mb-1">Body Font Size</span>
                      <select
                        value={activeSlide.fontSizeBody || 'text-sm'}
                        onChange={(e) => handleUpdateSlide({ ...activeSlide, fontSizeBody: e.target.value })}
                        className="w-full p-2 border border-slate-200 rounded-lg text-[10px] font-bold bg-[#f8fafc] text-slate-800 outline-none"
                      >
                        <option value="text-xs">XS (Extra Small)</option>
                        <option value="text-sm">SM (Standard)</option>
                        <option value="text-base">MD (Medium)</option>
                        <option value="text-lg">LG (Large)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Container Control */}
                <div className="pt-3 border-t border-slate-100">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Container Sizing Control</label>
                  <div className="grid grid-cols-2 gap-2 bg-[#f8fafc] border border-slate-100 p-1 rounded-xl">
                    <button
                      onClick={() => handleUpdateSlide({ ...activeSlide, containerSize: 'focused' })}
                      className={`py-2 rounded-lg text-[10px] font-bold transition-all ${
                        activeSlide.containerSize === 'focused'
                          ? 'bg-[#00a3e0] text-white shadow'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      Focused (Constrained)
                    </button>
                    <button
                      onClick={() => handleUpdateSlide({ ...activeSlide, containerSize: 'extended' })}
                      className={`py-2 rounded-lg text-[10px] font-bold transition-all ${
                        activeSlide.containerSize === 'extended'
                          ? 'bg-[#00a3e0] text-white shadow'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      Extended (Full-width)
                    </button>
                  </div>
                </div>

                {/* Alignment Matrix (X-axis, Y-axis) */}
                <div className="pt-3 border-t border-slate-100">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2.5">Alignment Matrix</label>
                  
                  <div className="space-y-3">
                    <div>
                      <span className="text-[8px] font-bold text-slate-400 uppercase block mb-1.5">Horizontal Alignment (X)</span>
                      <div className="flex gap-2 bg-[#f8fafc] border border-slate-100 p-1 rounded-lg">
                        {['left', 'center', 'right'].map((align) => (
                          <button
                            key={align}
                            onClick={() => handleUpdateSlide({ ...activeSlide, alignX: align as any })}
                            className={`flex-1 py-1 text-[9px] font-bold uppercase rounded transition-all ${
                              activeSlide.alignX === align
                                ? 'bg-[#00a3e0] text-white shadow'
                                : 'text-slate-400 hover:text-slate-600'
                            }`}
                          >
                            {align}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="text-[8px] font-bold text-slate-400 uppercase block mb-1.5">Vertical Alignment (Y)</span>
                      <div className="flex gap-2 bg-[#f8fafc] border border-slate-100 p-1 rounded-lg">
                        {['top', 'center', 'bottom'].map((align) => (
                          <button
                            key={align}
                            onClick={() => handleUpdateSlide({ ...activeSlide, alignY: align as any })}
                            className={`flex-1 py-1 text-[9px] font-bold uppercase rounded transition-all ${
                              activeSlide.alignY === align
                                ? 'bg-[#00a3e0] text-white shadow'
                                : 'text-slate-400 hover:text-slate-600'
                            }`}
                          >
                            {align}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Native Hex Color Pickers */}
                <div className="pt-4 border-t border-slate-100">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2.5">Slide Canvas Colors</label>
                  <div className="grid grid-cols-2 gap-3 bg-[#f8fafc] p-3 rounded-xl border border-slate-200">
                    <div>
                      <span className="text-[8px] font-bold text-slate-500 uppercase block mb-1">Background Hex</span>
                      <div className="flex items-center gap-1.5 bg-white rounded-lg p-1 border border-slate-200">
                        <input
                          type="color"
                          value={activeSlide.backgroundColor || '#FAF6F0'}
                          onChange={(e) => handleUpdateSlide({ ...activeSlide, backgroundColor: e.target.value })}
                          className="w-5 h-5 rounded cursor-pointer border border-transparent"
                        />
                        <input
                          type="text"
                          value={activeSlide.backgroundColor || '#FAF6F0'}
                          onChange={(e) => handleUpdateSlide({ ...activeSlide, backgroundColor: e.target.value })}
                          className="flex-1 w-full bg-transparent border-none text-[10px] text-slate-800 font-mono font-extrabold focus:outline-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-[8px] font-bold text-slate-500 uppercase block mb-1">Text Color Hex</span>
                      <div className="flex items-center gap-1.5 bg-white rounded-lg p-1 border border-slate-200">
                        <input
                          type="color"
                          value={activeSlide.textColor || '#1E293B'}
                          onChange={(e) => handleUpdateSlide({ ...activeSlide, textColor: e.target.value })}
                          className="w-5 h-5 rounded cursor-pointer border border-transparent"
                        />
                        <input
                          type="text"
                          value={activeSlide.textColor || '#1E293B'}
                          onChange={(e) => handleUpdateSlide({ ...activeSlide, textColor: e.target.value })}
                          className="flex-1 w-full bg-transparent border-none text-[10px] text-slate-800 font-mono font-extrabold focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gate Policy Settings */}
                <div className="pt-4 border-t border-slate-100 space-y-3.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Instructional Gate Policy</label>
                  
                  <div className="space-y-3 bg-[#f8fafc] p-3.5 rounded-xl border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-bold text-slate-800 block">Minimum View Lock</span>
                        <span className="text-[8px] text-slate-400 block mt-0.5">Locks the next stage slide button</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded-lg border border-slate-200">
                        <input
                          type="number"
                          min="0"
                          value={activeSlide.minViewTime || 0}
                          onChange={(e) => handleUpdateSlide({ ...activeSlide, minViewTime: parseInt(e.target.value) || 0 })}
                          className="w-10 bg-transparent text-center text-xs font-bold font-mono text-slate-800 focus:outline-none border-none"
                        />
                        <span className="text-[9px] font-black text-slate-400 uppercase">s</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2.5 border-t border-slate-100">
                      <div>
                        <span className="text-[10px] font-bold text-slate-800 block">Skippable Slide</span>
                        <span className="text-[8px] text-slate-400 block mt-0.5">Student can skip the view gate</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={activeSlide.skippable !== undefined ? activeSlide.skippable : true}
                          onChange={(e) => handleUpdateSlide({ ...activeSlide, skippable: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-8 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-400 after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#00a3e0]"></div>
                      </label>
                    </div>
                  </div>
                </div>

              </div>
            )}

          </div>

        </div>

      </div>

    </div>
  );
};
